/**
 * ScoringConfigOverrideAccordion
 *
 * In-page accordion that lets the user tune scoring configuration parameters
 * for a single execution / re-run. The override is NOT persisted — when the
 * user modifies any field, the parent receives the override object via
 * `onOverrideChange` and is responsible for attaching it to the request body
 * (typically as `score_config_params`). When the user has not modified
 * anything, `onOverrideChange(null)` is emitted so the parent can omit the
 * field and let the backend use system defaults.
 */
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Box,
  Grid,
  Typography,
  TextField,
  CircularProgress,
  Divider,
  Alert,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { scoringSeverityOptions } from "../../services/constants";
import { IconBtnEditBase } from "../../common/utils/icons";
import {
  ScoringType,
  PARAMS_MAP,
  SEVERITY_MAPPINGS_TYPES,
  SEVERITY_MAPPING_NAME_LABEL,
  cleanMappings,
} from "./ScoringOverrideDialog";
import {
  getValidationScoringConfigDefaults,
  getComparisonScoringConfigDefaults,
} from "../../services/scoringService";
import { validateScoringValues } from "../../utils/scoringConfigFormValidation";

const TITLE_MAP: Record<ScoringType, string> = {
  validation: "Validation Scoring Configuration Overrides",
  comparison: "Comparison Scoring Configuration Overrides",
  generic_rq: "Generic Research Query Scoring Configuration Overrides",
};

interface IScoringConfigOverrideAccordionProps {
  type: ScoringType;
  /** Visibility toggle — typically `runParameters.generate_score`. */
  generateScore: boolean;
  /** Optional config snapshot from a prior execution to seed initial values. */
  currentConfig?: Record<string, any> | null;
  /** Controlled expansion state (matches sibling accordions on the page). */
  expanded: boolean;
  /** Standard MUI Accordion change handler from the parent. */
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
  /**
   * Called whenever the override changes. `null` means the user has not
   * modified anything (parent should NOT include `score_config_params` in
   * the request). A non-null object is the full flat override payload to be
   * sent under `score_config_params`.
   */
  onOverrideChange: (override: Record<string, any> | null) => void;
  /** Disables all inputs while the parent is submitting / loading. */
  disabled?: boolean;
  /**
   * Optional list of selectable names for the severity-mappings name column
   * (comparison check names sourced from the rule definition, or generic
   * research-query names sourced from the research query definition). When
   * provided and the type supports severity mappings, renders a searchable
   * Autocomplete instead of a plain TextField, mirroring the corresponding
   * Edit screens.
   */
  nameOptions?: string[];
}

const fetchDefaultsFor = async (
  type: ScoringType,
): Promise<Record<string, any>> => {
  // GRQ shares validation parameter shape — reuse the validation defaults
  // endpoint to avoid an additional backend dependency.
  if (type === "comparison") {
    return (await getComparisonScoringConfigDefaults()) ?? {};
  }
  return (await getValidationScoringConfigDefaults()) ?? {};
};

const ScoringConfigOverrideAccordion: React.FC<
  IScoringConfigOverrideAccordionProps
> = ({
  type,
  generateScore,
  currentConfig,
  expanded,
  onChange,
  onOverrideChange,
  disabled = false,
  nameOptions,
}) => {
  const params = PARAMS_MAP[type];
  const supportsSeverityMappings = SEVERITY_MAPPINGS_TYPES.has(type);
  const useNameAutocomplete =
    supportsSeverityMappings &&
    Array.isArray(nameOptions) &&
    nameOptions.length > 0;

  const [defaults, setDefaults] = useState<Record<string, any> | null>(null);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
  // Committed state — what view-mode shows and what is emitted as the override.
  const [committedValues, setCommittedValues] = useState<
    Record<string, number>
  >({});
  const [committedMappings, setCommittedMappings] = useState<
    Record<string, string>
  >({});
  // Edit-mode local form state — only promoted to committed on Save.
  const [formValues, setFormValues] = useState<Record<string, number>>({});
  const [formMappings, setFormMappings] = useState<Record<string, string>>({});
  const [modified, setModified] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const newMappingCounter = useRef(0);

  const baseConfig =
    currentConfig && typeof currentConfig === "object"
      ? currentConfig
      : (defaults ?? {});

  const buildInitialMappings = useCallback((): Record<string, string> => {
    if (!supportsSeverityMappings) return {};
    const src =
      (baseConfig as any)?.severity_mappings &&
      typeof (baseConfig as any).severity_mappings === "object"
        ? (baseConfig as any).severity_mappings
        : {};
    return { ...src };
  }, [baseConfig, supportsSeverityMappings]);

  const seedFromBase = useCallback(() => {
    const initial: Record<string, number> = {};
    params.forEach((p) => {
      initial[p.key] = baseConfig[p.key] ?? defaults?.[p.key] ?? 0;
    });
    const initialMappings = buildInitialMappings();
    setCommittedValues(initial);
    setCommittedMappings(initialMappings);
    setFormValues(initial);
    setFormMappings(initialMappings);
    setModified(false);
    setIsEdit(false);
    newMappingCounter.current = 0;
  }, [baseConfig, buildInitialMappings, defaults, params]);

  // Lazy-load defaults the first time the accordion becomes visible. When
  // `generateScore` is false we render nothing and skip the API call entirely.
  useEffect(() => {
    if (!generateScore) return;
    if (defaults !== null) return;
    let cancelled = false;
    setIsLoadingDefaults(true);
    fetchDefaultsFor(type)
      .then((data) => {
        if (!cancelled) setDefaults(data ?? {});
      })
      .finally(() => {
        if (!cancelled) setIsLoadingDefaults(false);
      });
    return () => {
      cancelled = true;
    };
  }, [generateScore, type, defaults]);

  // Seed values once we have a baseConfig (snapshot or fetched defaults).
  useEffect(() => {
    if (!generateScore) return;
    if (!currentConfig && defaults === null) return;
    seedFromBase();
    // Intentionally exclude seedFromBase from deps to avoid re-seeding on
    // every render and clobbering user-saved overrides.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generateScore, type, currentConfig, defaults]);

  // Reset state when the section is toggled off so toggling back on starts
  // from a clean baseline (no stale modifications carried over).
  useEffect(() => {
    if (generateScore) return;
    setCommittedValues({});
    setCommittedMappings({});
    setFormValues({});
    setFormMappings({});
    setModified(false);
    setIsEdit(false);
    newMappingCounter.current = 0;
    onOverrideChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generateScore]);

  // Push the override (or null) up to the parent whenever the committed
  // (saved) state changes. This is the single source of truth for the
  // parent's `score_config_params` field — it is only non-null after the
  // user has explicitly clicked Save in the accordion.
  useEffect(() => {
    if (!generateScore) return;
    if (!modified) {
      onOverrideChange(null);
      return;
    }
    const override: Record<string, any> = {};
    params.forEach((p) => {
      override[p.key] = committedValues[p.key];
    });
    if (supportsSeverityMappings) {
      override.severity_mappings = cleanMappings(committedMappings);
    }
    onOverrideChange(override);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modified, committedValues, committedMappings, generateScore]);

  const handleEnterEdit = () => {
    // Pre-fill the form with the currently displayed (committed) state.
    setFormValues({ ...committedValues });
    setFormMappings({ ...committedMappings });
    setIsEdit(true);
  };

  const handleCancel = () => {
    setFormValues({ ...committedValues });
    setFormMappings({ ...committedMappings });
    setIsEdit(false);
  };

  const handleSave = () => {
    const currentValidation = validateScoringValues(params, formValues);
    const hasCurrentErrors =
      Object.keys(currentValidation.fieldErrors).length > 0 ||
      Boolean(currentValidation.impactSumError) ||
      Boolean(currentValidation.categoryWeightSumError);
    if (hasCurrentErrors || hasInvalidMappingRow) return;
    const cleanedMappings = supportsSeverityMappings
      ? cleanMappings(formMappings)
      : {};
    setCommittedValues({ ...formValues });
    setCommittedMappings(cleanedMappings);
    setFormMappings(cleanedMappings);
    setModified(true);
    setIsEdit(false);
  };

  const handleChangeValue = (key: string, val: number) => {
    setFormValues((prev) => ({ ...prev, [key]: val }));
  };

  const handleAddMapping = () => {
    const tempKey = `__new_${newMappingCounter.current++}`;
    setFormMappings((prev) => ({ ...prev, [tempKey]: "medium" }));
  };

  const handleRemoveMapping = (name: string) => {
    setFormMappings((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleUpdateMappingName = (oldName: string, newName: string) => {
    if (newName === oldName) return;
    setFormMappings((prev) => {
      const next: Record<string, string> = {};
      Object.entries(prev).forEach(([k, v]) => {
        next[k === oldName ? newName : k] = v;
      });
      return next;
    });
  };

  const handleUpdateMappingSeverity = (name: string, severity: string) => {
    setFormMappings((prev) => ({ ...prev, [name]: severity }));
  };

  const hasInvalidMappingRow = supportsSeverityMappings
    ? Object.keys(formMappings).some((k) => k === "" || k.startsWith("__new_"))
    : false;

  const hasChanges =
    JSON.stringify(formValues) !== JSON.stringify(committedValues) ||
    (supportsSeverityMappings &&
      JSON.stringify(cleanMappings(formMappings)) !==
        JSON.stringify(committedMappings));

  const validation = validateScoringValues(params, formValues);
  const hasScoringErrors =
    Object.keys(validation.fieldErrors).length > 0 ||
    Boolean(validation.impactSumError) ||
    Boolean(validation.categoryWeightSumError);

  if (!generateScore) return null;

  return (
    <Accordion
      className="mt-8 heading-bold box-shadow color-palette color-palette-1"
      expanded={expanded}
      onChange={onChange}
    >
      <AccordionSummary
        aria-controls="scoring-config-override-content"
        id="scoring-config-override-header"
        expandIcon={<ExpandMoreIcon />}
      >
        {TITLE_MAP[type]}
      </AccordionSummary>
      <AccordionDetails sx={{ paddingTop: "16px" }}>
        {isLoadingDefaults && defaults === null ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading scoring defaults…
            </Typography>
          </Box>
        ) : !isEdit ? (
          <>
            {modified && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Parameters modified — these overrides will only apply to this
                single execution.
              </Alert>
            )}
            <Grid container rowSpacing={1.5} columnSpacing={2.5}>
              {params.map((p) => (
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3} key={p.key}>
                  <label className="label-text text-bold">{p.label}</label>
                  <div className="form-control">
                    {committedValues[p.key] ?? "N/A"}
                  </div>
                </Grid>
              ))}
            </Grid>

            {supportsSeverityMappings && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                  Severity Mappings
                </Typography>
                {Object.keys(committedMappings).length > 0 && (
                  <TableContainer
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{ fontWeight: 700, fontSize: "0.75rem" }}
                          >
                            {SEVERITY_MAPPING_NAME_LABEL[type]}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              width: 140,
                            }}
                          >
                            Severity
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(committedMappings).map(
                          ([name, severity]) => (
                            <TableRow key={name}>
                              <TableCell>{name}</TableCell>
                              <TableCell sx={{ width: 140 }}>
                                {severity}
                              </TableCell>
                            </TableRow>
                          ),
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* Edit icon — placed at the bottom so it sits below the severity
                mappings table (when present) instead of between the params and
                the severity mappings. */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <button
                type="button"
                className="btn-nostyle icon-btn-edit"
                onClick={handleEnterEdit}
                disabled={disabled}
              >
                <IconBtnEditBase ariaLabel="Edit scoring configuration overrides" />
              </button>
            </Box>
          </>
        ) : (
          <>
            <Grid container rowSpacing={1.5} columnSpacing={2.5}>
              {params.map((p) => (
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3} key={p.key}>
                  <TextField
                    type="number"
                    name={p.key}
                    fullWidth
                    label={p.label}
                    variant="outlined"
                    className="form-control-autocomplete"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const inputValue = event.target.value;
                      if (inputValue === "") {
                        handleChangeValue(p.key, NaN);
                        return;
                      }
                      const v = parseFloat(inputValue);
                      if (!isNaN(v)) handleChangeValue(p.key, v);
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: p.min, max: p.max, step: p.step }}
                    value={
                      formValues[p.key] === undefined ||
                      Number.isNaN(formValues[p.key])
                        ? ""
                        : formValues[p.key]
                    }
                    disabled={disabled}
                    error={Boolean(validation.fieldErrors[p.key])}
                    helperText={validation.fieldErrors[p.key] || " "}
                  />
                </Grid>
              ))}
            </Grid>

            {validation.impactSumError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {validation.impactSumError}
              </Alert>
            )}

            {validation.categoryWeightSumError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {validation.categoryWeightSumError}
              </Alert>
            )}

            {supportsSeverityMappings && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" fontWeight={700}>
                    Severity Mappings
                  </Typography>
                  <Button
                    size="small"
                    className="btn-orange btn-sm"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddMapping}
                    disabled={disabled}
                  >
                    Add Mapping
                  </Button>
                </Box>
                {Object.keys(formMappings).length > 0 && (
                  <TableContainer
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{ fontWeight: 700, fontSize: "0.75rem" }}
                          >
                            {SEVERITY_MAPPING_NAME_LABEL[type]}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              width: 140,
                            }}
                          >
                            Severity
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ width: 48, p: 0.5 }}
                          />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(formMappings).map(
                          ([name, severity]) => {
                            const isTemp = name.startsWith("__new_");
                            const usedNames = Object.keys(formMappings).filter(
                              (n) => n !== name,
                            );
                            const availableOptions = useNameAutocomplete
                              ? (nameOptions as string[]).filter(
                                  (opt) => !usedNames.includes(opt),
                                )
                              : [];
                            return (
                              <TableRow key={name}>
                                <TableCell>
                                  {useNameAutocomplete ? (
                                    <Autocomplete
                                      size="small"
                                      value={isTemp ? null : name || null}
                                      options={availableOptions}
                                      disabled={disabled}
                                      onChange={(_e, newValue) =>
                                        handleUpdateMappingName(
                                          name,
                                          newValue || "",
                                        )
                                      }
                                      className="form-control-autocomplete dropdown-padding-1"
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          className="form-control"
                                          placeholder={
                                            SEVERITY_MAPPING_NAME_LABEL[type]
                                          }
                                        />
                                      )}
                                    />
                                  ) : (
                                    <TextField
                                      size="small"
                                      fullWidth
                                      placeholder={
                                        SEVERITY_MAPPING_NAME_LABEL[type]
                                      }
                                      value={isTemp ? "" : name}
                                      onChange={(e) =>
                                        handleUpdateMappingName(
                                          name,
                                          e.target.value,
                                        )
                                      }
                                      disabled={disabled}
                                    />
                                  )}
                                </TableCell>
                                <TableCell sx={{ width: 140 }}>
                                  <Select
                                    size="small"
                                    fullWidth
                                    value={severity}
                                    onChange={(e) =>
                                      handleUpdateMappingSeverity(
                                        name,
                                        e.target.value as string,
                                      )
                                    }
                                    disabled={disabled}
                                  >
                                    {scoringSeverityOptions.map((opt) => (
                                      <MenuItem key={opt} value={opt}>
                                        {opt}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ width: 48, p: 0.5 }}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveMapping(name)}
                                    disabled={disabled}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          },
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                {hasInvalidMappingRow && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Please provide a name for every severity mapping row.
                  </Alert>
                )}
              </Box>
            )}

            <Grid
              container
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "flex-end",
                columnGap: "20px",
                mt: 2,
              }}
            >
              <Button
                color="secondary"
                variant="contained"
                onClick={handleCancel}
                className="btn-orange btn-dark"
                disabled={disabled}
              >
                Cancel
              </Button>
              <Button
                type="button"
                color="secondary"
                variant="contained"
                className="btn-orange"
                onClick={handleSave}
                disabled={
                  disabled ||
                  !hasChanges ||
                  hasInvalidMappingRow ||
                  hasScoringErrors
                }
              >
                <SaveOutlinedIcon /> &nbsp; Save
              </Button>
            </Grid>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default ScoringConfigOverrideAccordion;
